import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { toast } from 'react-hot-toast';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000, // Reduced timeout to 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

const MIC_MUTATION_ALLOW = [
  /\/auth\/logout/i,
  /\/auth\/change-password/i,
  /\/users\/profile/i,
  /\/users\/add-profile/i,
];

function isMicBlockedRequest(config) {
  const role = localStorage.getItem("userRole");
  if (role !== "MIC") return false;
  const method = String(config.method || "get").toUpperCase();
  const url = String(config.url || "");
  // Proposal PDFs embed raw contacts — block only those; CSV/Excel exports are allowed (masked).
  if (/\/opportunities\/pdf(?:\/|\?|$)/i.test(url)) return true;
  if (method === "GET" || method === "HEAD" || method === "OPTIONS") return false;
  if (MIC_MUTATION_ALLOW.some((re) => re.test(url))) return false;
  return true;
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    if (isMicBlockedRequest(config)) {
      const err = new Error("MIC role is read-only. This action is not permitted.");
      err.response = {
        status: 403,
        data: { success: false, message: err.message },
      };
      toast.error(err.message);
      return Promise.reject(err);
    }

    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and performance monitoring
apiClient.interceptors.response.use(
  (response) => {
    // Log response time for performance monitoring
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;
    
    if (duration > 2000) {
      console.warn(`Slow API response: ${response.config.url} took ${duration}ms`);
    }
    
    return response;
  },
  (error) => {
    // Log error response time
    if (error.config?.metadata) {
      const endTime = new Date();
      const duration = endTime - error.config.metadata.startTime;
      console.error(`API Error after ${duration}ms:`, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
      });
    }
    
    // Handle common errors
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('columnVisibilityClient');
      localStorage.removeItem('columnVisibilityProperty');
      
      // Show error message
      toast.error('Session expired. Please login again.');
      
      // Redirect to login
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    if (error.response?.status === 403) {
      toast.error('Access forbidden. You do not have permission.');
    }
    
    if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    }
    
    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please check your connection.');
    }
    
    return Promise.reject(error);
  }
);

// Helper function for handling API errors
export const handleApiError = (error, showToast = true) => {
  const message = error.response?.data?.message || 'An error occurred';
  
  if (showToast && error.response?.status !== 401) {
    toast.error(message);
  }
  
  return message;
};

// Generic API methods
export const apiService = {
  // GET request
  get: async (url, config = {}) => {
    const response = await apiClient.get(url, config);
    return response.data;
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
    const headers = { ...(config.headers || {}) };
    if (isFormData) {
      delete headers["Content-Type"];
    }
    const response = await apiClient.post(url, data, { ...config, headers });
    return response.data;
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    const response = await apiClient.put(url, data, config);
    return response.data;
  },

  // DELETE request
  delete: async (url, config = {}) => {
    const response = await apiClient.delete(url, config);
    return response.data;
  },

  // GET request that returns the raw axios response (including headers).
  // Useful for binary downloads where the caller needs Content-Disposition,
  // Content-Type, etc. Goes through the same interceptor so auth + 401
  // handling stay consistent with normal calls.
  getBlob: async (url, config = {}) => {
    return apiClient.get(url, { ...config, responseType: 'blob' });
  },

  // PATCH request
  patch: async (url, data = {}, config = {}) => {
    const response = await apiClient.patch(url, data, config);
    return response.data;
  }
};

export default apiClient;